/** Compact server cursors that move whenever something that can change this
 * wallet’s private balance happens: the owner’s inbox lanes and journal on the
 * data service, the owner’s fee-recovery feed on the relayer, and the shared
 * pool event index. No ciphertext and no note identities travel with them. */
export interface SyncHeadReading {
  /** False when any source cannot vouch for its cursor (index not verified,
   * request failed): an unauthoritative head never proves that nothing changed. */
  authoritative: boolean;
  cursors: unknown;
  /** Verified index position; informational, never part of the fingerprint. */
  chain: { indexedBlock: number | null; indexedBlockHash: string | null };
}

export interface SyncHeadRecord {
  version: 1;
  fingerprint: string;
  syncedAt: number;
}

export interface SyncHeadCheck {
  /** Every cursor equals the last committed sync and the reading is authoritative. */
  unchanged: boolean;
  /** Authoritative, and nothing was ever delivered to this wallet (see
   * syncHeadIsEmpty). */
  empty: boolean;
  fingerprint: string;
  reading: SyncHeadReading;
  /** Persist the cursors observed before this refresh started. Anything that
   * happened after the read moves a cursor, so the next check sees it. */
  commit: () => Promise<void>;
}

interface DataHeadResponse {
  success?: unknown;
  mailbox?: unknown;
  journal?: unknown;
  chain?: {
    ready?: unknown;
    generation?: unknown;
    currentTree?: unknown;
    nextLeaf?: unknown;
    lastEvent?: unknown;
    indexedBlock?: unknown;
    indexedBlockHash?: unknown;
    [diagnostic: string]: unknown;
  } | null;
  [diagnostic: string]: unknown;
}

interface RelayerHeadResponse {
  pool: string;
  success?: unknown;
  epoch?: unknown;
  position?: unknown;
  count?: unknown;
}

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => [key, stable((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

/** Leaves out the legacy inbox's spent-marker count. A refresh writes those
 * markers itself after validating (marking spent notes), so counting them
 * made every such refresh force a slower next one. A marker only hides an
 * inbox copy; spent state comes from chain nullifiers, which the chain
 * cursors cover. */
function withoutSpentMarkers(cursors: unknown): unknown {
  const legacy = (cursors as { mailbox?: { legacy?: unknown } } | null)?.mailbox
    ?.legacy;
  if (!legacy || typeof legacy !== "object") return cursors;
  const { spentMarkers: _spentMarkers, ...rest } = legacy as Record<
    string,
    unknown
  >;
  const value = cursors as { mailbox: Record<string, unknown> };
  return { ...value, mailbox: { ...value.mailbox, legacy: rest } };
}

/** Order-independent canonical form of the cursors. It is compared, never
 * displayed, and only ever stored inside the encrypted per-wallet cache. */
export function syncHeadFingerprint(cursors: unknown): string {
  return JSON.stringify(stable(withoutSpentMarkers(cursors)));
}

const finite = (value: unknown) =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Build a reading from the raw service responses, keeping only cursor fields
 * so volatile metadata (timestamps, diagnostics) never changes the fingerprint. */
export function syncHeadReadingFromResponses(
  data: DataHeadResponse | null | undefined,
  relayer: readonly RelayerHeadResponse[],
): SyncHeadReading {
  const chain = data?.chain ?? null;
  const chainReady =
    !!chain &&
    chain.ready === true &&
    finite(chain.generation) &&
    finite(chain.currentTree) &&
    finite(chain.nextLeaf);
  const relayerOk = relayer.every(
    (head) =>
      head.success === true &&
      typeof head.epoch === "string" &&
      finite(head.position) &&
      finite(head.count),
  );
  const authoritative =
    !!data &&
    data.success === true &&
    !!data.mailbox &&
    !!data.journal &&
    chainReady &&
    relayerOk;
  return {
    authoritative,
    chain: {
      indexedBlock: finite(chain?.indexedBlock)
        ? (chain!.indexedBlock as number)
        : null,
      indexedBlockHash:
        typeof chain?.indexedBlockHash === "string" &&
        /^0x[0-9a-f]{64}$/i.test(chain.indexedBlockHash)
          ? chain.indexedBlockHash.toLowerCase()
          : null,
    },
    cursors: {
      mailbox: data?.mailbox ?? null,
      journal: data?.journal ?? null,
      chain: chain
        ? {
            generation: chain.generation ?? null,
            currentTree: chain.currentTree ?? null,
            nextLeaf: chain.nextLeaf ?? null,
            lastEvent: chain.lastEvent ?? null,
          }
        : null,
      relayer: [...relayer]
        .map((head) => ({
          pool: head.pool.toLowerCase(),
          epoch: head.epoch ?? null,
          position: head.position ?? null,
          count: head.count ?? null,
        }))
        .sort((a, b) => a.pool.localeCompare(b.pool)),
    },
  };
}

/** The authoritative head shows nothing at all for this wallet: no delivery in
 * any inbox lane (pending, protected, archive, legacy), no journal record and
 * no fee-recovery entry. Flat notes only ever reach a wallet through these
 * feeds, so a wallet that holds no local note either has nothing to download
 * or validate yet. A missing count never counts as zero. */
export function syncHeadIsEmpty(reading: SyncHeadReading): boolean {
  if (!reading.authoritative) return false;
  const cursors = reading.cursors as {
    mailbox?: Record<string, { records?: unknown } | null | undefined> | null;
    journal?: { records?: unknown } | null;
    relayer?: { count?: unknown }[];
  } | null;
  const mailbox = cursors?.mailbox;
  return (
    !!mailbox &&
    ["pending", "protected", "archive", "legacy"].every(
      (lane) => mailbox[lane]?.records === 0,
    ) &&
    cursors?.journal?.records === 0 &&
    Array.isArray(cursors.relayer) &&
    cursors.relayer.every((head) => head.count === 0)
  );
}

export async function evaluateSyncHead(options: {
  read: (signal?: AbortSignal) => Promise<SyncHeadReading>;
  load: () => Promise<SyncHeadRecord | null>;
  save: (record: SyncHeadRecord) => Promise<void>;
  signal?: AbortSignal;
  now?: () => number;
}): Promise<SyncHeadCheck> {
  const reading = await options.read(options.signal);
  const fingerprint = syncHeadFingerprint(reading.cursors);
  let saved: SyncHeadRecord | null = null;
  try {
    saved = await options.load();
  } catch {
    saved = null;
  }
  const unchanged =
    reading.authoritative &&
    saved?.version === 1 &&
    saved.fingerprint === fingerprint;
  return {
    unchanged,
    empty: syncHeadIsEmpty(reading),
    fingerprint,
    reading,
    commit: async () => {
      // Only an authoritative reading may become the baseline; otherwise the
      // next refresh must validate in full again.
      if (!reading.authoritative) return;
      await options.save({
        version: 1,
        fingerprint,
        syncedAt: (options.now ?? Date.now)(),
      });
    },
  };
}
